import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import * as moment from 'moment';
import { SelectItem } from 'primeng/api';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { GENDERS } from 'projects/core/src/lib/constants';

export interface AcComponentFilterValuesType {
  templates?: Array<number> | null;
  start?: any | null;
  end?: any | null;
  gender?: string | null;
  candidatetype?: string | null;
  competences?: Array<number> | null;
  exercises?: Array<number> | null;
  onlinetests?: Array<number> | null;
  recommendations?: Array<number> | null;
}

@Component({
  selector: 'app-ac-competence-filter',
  templateUrl: './ac-competence-filter.component.html',
  styleUrls: ['./ac-competence-filter.component.scss']
})
export class AcCompetenceFilterComponent implements OnInit, OnChanges {

  @Input() values: AcComponentFilterValuesType = null;
  @Output() filterSet = new EventEmitter<AcComponentFilterValuesType>();

  loading = true;
  language: string;
  form: UntypedFormGroup = this.fb.group({
    templates: new UntypedFormControl(null, [Validators.required]),
    start: new UntypedFormControl(null),
    end: new UntypedFormControl(null),
    gender: new UntypedFormControl(null),
    competences: new UntypedFormControl(null, [Validators.required]),
    exercises: new UntypedFormControl(null),
    onlinetests: new UntypedFormControl(null),
    candidatetype: new UntypedFormControl(null),
    recommendations: new UntypedFormControl(null),
  });

  optionsTemplates: Array<SelectItem> = [];
  optionsGenders: Array<SelectItem> = [];
  optionsCompetences: Array<SelectItem> = [];
  optionsExercises: Array<SelectItem> = [];
  optionsOnlinetests: Array<SelectItem> = [];
  optionsCandidatetypes: Array<SelectItem> | null = null;
  optionsRecommendations: Array<SelectItem> | null = null;

  minDate: any = null;
  maxDate: any = null;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.language = this.transloco.getActiveLang().toUpperCase();
    this.optionsGenders = FormHelper.getSelectList(GENDERS, null, this.transloco);
    this.api.getStatisticsFilters('ac/competences').subscribe({
      next: (result: any) => {
        this.optionsTemplates = result.data.templates;
        this.optionsCompetences = result.data.competences;
        this.optionsExercises = result.data.exercises;
        this.optionsOnlinetests = result.data.onlinetests;
        this.optionsCandidatetypes = result.data.candidatetypes?.map((candidatetype: string) => {
          return {
            label: this.transloco.translate('common.options.candidatetype.' + candidatetype),
            value: candidatetype
          };
        }) || null;
        this.optionsRecommendations = result.data.recommendations || null;
        if (result.data.period?.start) {
          this.minDate = moment('1970-01-01').year(result.data.period?.start).toDate();
        }
        if (result.data.period?.end) {
          this.maxDate = moment('1970-01-01').year(result.data.period?.end).endOf('year').toDate();
        }
        this.initForm();
      }
    }).add(() => {
      this.loading = false;
    });
  }

  ngOnChanges(): void {
    this.initForm();
  }

  onResetForm(): void {
    this.initForm();
    this.filterSet.emit(null);
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.filterSet.emit(this.getFilterValues());
    } else {
      FormHelper.markFormAsDirty(this.form);
    }
  }

  setDetailFilters(filters: any): void {
    this.form.patchValue({
      competences: this.optionsCompetences.filter((competence: any) => {
        return filters.competence === null || filters.competence === 'overall' || filters.competence?.id === competence.id;
      }),
      exercises: this.optionsExercises.filter((exercise: any) => {
        return filters.element === null || filters.element === 'overall' || filters.element === 'exercises';
      }),
      onlinetests: this.optionsOnlinetests.filter((onlinetest: any) => {
        return filters.element === null || filters.element === 'overall' || filters.element === onlinetest.name;
      })
    });
  }

  private getFilterValues(): AcComponentFilterValuesType {
    return {
      templates: this.form.get('templates').value?.map((template: any) => template.id) || null,
      start: this.form.get('start').value,
      end: this.form.get('end').value,
      gender: this.form.get('gender').value,
      candidatetype: this.form.get('candidatetype').value,
      competences: this.form.get('competences').value?.map((entry: any) => {
        return entry.id;
      }) || null,
      exercises: this.form.get('exercises').value?.map((entry: any) => {
        return entry.id;
      }) || null,
      onlinetests: this.form.get('onlinetests').value?.map((entry: any) => {
        return entry.id;
      }) || null,
      recommendations: this.form.get('recommendations').value?.map((entry: any) => {
        return entry.id;
      }) || null,
    };
  }

  private initForm(): void {
    this.form.patchValue({
      templates: (this.values?.templates) ? this.optionsTemplates.filter((template: any) => this.values?.templates?.includes(template.id)) : null,
      start: this.values?.start || null,
      end: this.values?.end || null,
      gender: (this.values?.gender) ? this.optionsGenders.find((gender: any) => gender.value === this.values?.gender).value : null,
      candidatetype: (this.values?.candidatetype) ? this.optionsCandidatetypes.find((candidatetype: SelectItem) => candidatetype.value === this.values?.candidatetype).value : null,
      competences: this.optionsCompetences.filter((competence: any) => {
        return this.values === null || this.values?.competences?.indexOf(competence.id) !== -1;
      }),
      exercises: this.optionsExercises.filter((exercise: any) => {
        return this.values === null || this.values?.exercises?.indexOf(exercise.id) !== -1;
      }),
      onlinetests: this.optionsOnlinetests.filter((onlinetest: any) => {
        return this.values === null || this.values?.onlinetests?.indexOf(onlinetest.id) !== -1;
      }),
      recommendations: this.optionsRecommendations?.filter((recommendation: any) => {
        return this.values === null || this.values?.recommendations?.indexOf(recommendation.id) !== -1;
      })
    });
  }

}
